import { IconButton } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import {
  FilterDropdownWrapper,
  FilterSelectControl,
  FramerTooltip,
  selectStyles,
  selectStylesMobile,
} from "@ryde/controls";
import { usePageContext, useReportsContext } from "@ryde/redux";
import {
  getGridLabelKey,
  IDropdownItem,
  reportFiltersWithValuesAF,
  useIsMobile,
} from "@ryde/shared";

import { useTableFiltersActionsReports } from "./use-table-filters-actions-reports";

export const FilterAddButtonReports: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { reportId, reportTemplate } = useReportsContext();
  const { isMobile } = useIsMobile();
  const templateFilters = reportTemplate?.availableFilters;
  const activeFilters = useRecoilValue(reportFiltersWithValuesAF(reportId));

  const availableToAddFilters = useMemo(() => {
    return Object.keys(templateFilters || {}).filter(
      key => !(key in activeFilters),
    );
  }, [activeFilters, templateFilters]);

  const options = useMemo(() => {
    return availableToAddFilters.map(templateKey => ({
      value: templateKey,
      label: t(getGridLabelKey(entity, templateKey)),
    })) as IDropdownItem[];
  }, [availableToAddFilters, entity, t]);

  const { handleAddFilter } = useTableFiltersActionsReports();
  const [isOpen, setIsOpen] = useState(false);

  const addFilterHandler = useEvent((e: unknown) => {
    handleAddFilter(
      (e as IDropdownItem[] | undefined)?.[0]?.value as string | undefined,
    );
    setIsOpen(false);
  });

  const handleToggle = useEvent(() => setIsOpen(prev => !prev));
  const handleClose = useEvent(() => setIsOpen(false));

  const stylesComputed = useMemo(() => {
    return isMobile ? selectStylesMobile : selectStyles;
  }, [isMobile]);

  return (
    <FilterDropdownWrapper
      isFilterItem={false}
      isOpen={isOpen}
      onClose={handleClose}
      target={
        <FramerTooltip content={t("general.ADD_NEW_FILTER")}>
          <IconButton data-e2e="svg-add-filter" onClick={handleToggle}>
            <PIC.PlusSvg />
          </IconButton>
        </FramerTooltip>
      }
    >
      <Select
        isMulti
        // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
        autoFocus
        backspaceRemovesValue={false}
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Control: FilterSelectControl,
        }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={true}
        menuIsOpen
        options={options}
        onChange={addFilterHandler}
        placeholder={`${t(`general.SEARCH`)}...`}
        styles={stylesComputed}
        tabSelectsValue={false}
        noOptionsMessage={() => t(`errors.noOptionsMessage`)}
      />
    </FilterDropdownWrapper>
  );
};
